'use client';

import { FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { SearchField } from '@odys/design-system/components/SearchField';
import { useTranslations } from 'next-intl';

import type { DmcCardDto, DmcVisitorDto } from '@/app/api/lib/dmcs/dmc.types';
import {
    type DestinationFilterType,
    isSearchStarted,
    trackDestinationFilter,
} from '@/configurations/tracking/filters';

type SortKey = 'relevance' | 'name-asc' | 'name-desc' | 'product-desc';

export type DestinationFiltersValue = {
    search: string;
    specialty: string;
    market: string;
    sort: SortKey;
};

export const DEFAULT_FILTERS: DestinationFiltersValue = {
    search: '',
    specialty: '',
    market: '',
    sort: 'relevance',
};

type DmcOption = { slug: string; label: string };

type DestinationFiltersProps = {
    destinationSlug: string;
    value: DestinationFiltersValue;
    onChange: (next: DestinationFiltersValue) => void;
    dmcs: ReadonlyArray<DmcCardDto | DmcVisitorDto>;
    /** Whether the current viewer can sort by product count (Agency only). */
    canSortByProducts: boolean;
};

function buildSpecialtyOptions(dmcs: ReadonlyArray<DmcCardDto | DmcVisitorDto>): DmcOption[] {
    const seen = new Map<string, string>();
    for (const dmc of dmcs) {
        for (const specialty of dmc.specialties) {
            if (!seen.has(specialty.slug)) seen.set(specialty.slug, specialty.label);
        }
    }
    return Array.from(seen, ([slug, label]) => ({ slug, label })).sort((a, b) =>
        a.label.localeCompare(b.label, 'fr'),
    );
}

function buildMarketOptions(dmcs: ReadonlyArray<DmcCardDto | DmcVisitorDto>): DmcOption[] {
    const seen = new Set<string>();
    for (const dmc of dmcs) {
        for (const market of dmc.targetMarket) seen.add(market);
    }
    return Array.from(seen, (market) => ({ slug: market, label: market })).sort((a, b) =>
        a.label.localeCompare(b.label, 'fr'),
    );
}

export function DestinationFilters({
    destinationSlug,
    value,
    onChange,
    dmcs,
    canSortByProducts,
}: DestinationFiltersProps) {
    const t = useTranslations('destinationDetail.filters');

    const applyFilter = (filterType: DestinationFilterType, next: DestinationFiltersValue) => {
        onChange(next);
        trackDestinationFilter(destinationSlug, filterType);
    };

    const specialtyOptions = buildSpecialtyOptions(dmcs);
    const marketOptions = buildMarketOptions(dmcs);

    return (
        <Stack
            data-testid="destination-filters"
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'center' } }}
        >
            <SearchField
                value={value.search}
                onChange={(search) => {
                    onChange({ ...value, search });
                    if (isSearchStarted(value.search, search)) {
                        trackDestinationFilter(destinationSlug, 'search');
                    }
                }}
                placeholder={t('searchPlaceholder')}
                fullWidth
                sx={{ flex: 2 }}
                data-testid="destination-filter-search"
            />

            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: 0, md: 180 } }}>
                <InputLabel id="dest-filter-specialty">{t('specialty')}</InputLabel>
                <Select
                    labelId="dest-filter-specialty"
                    label={t('specialty')}
                    value={value.specialty}
                    onChange={(e) =>
                        applyFilter('specialty', { ...value, specialty: e.target.value })
                    }
                    data-testid="destination-filter-specialty"
                >
                    <MenuItem value="">{t('any')}</MenuItem>
                    {specialtyOptions.map((opt) => (
                        <MenuItem key={opt.slug} value={opt.slug}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: 0, md: 180 } }}>
                <InputLabel id="dest-filter-market">{t('market')}</InputLabel>
                <Select
                    labelId="dest-filter-market"
                    label={t('market')}
                    value={value.market}
                    onChange={(e) => applyFilter('market', { ...value, market: e.target.value })}
                    data-testid="destination-filter-market"
                >
                    <MenuItem value="">{t('any')}</MenuItem>
                    {marketOptions.map((opt) => (
                        <MenuItem key={opt.slug} value={opt.slug}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1, minWidth: { xs: 0, md: 180 } }}>
                <InputLabel id="dest-filter-sort">{t('sort')}</InputLabel>
                <Select
                    labelId="dest-filter-sort"
                    label={t('sort')}
                    value={value.sort}
                    onChange={(e) =>
                        applyFilter('sort', { ...value, sort: e.target.value as SortKey })
                    }
                    data-testid="destination-filter-sort"
                >
                    <MenuItem value="relevance">{t('sortRelevance')}</MenuItem>
                    <MenuItem value="name-asc">{t('sortNameAsc')}</MenuItem>
                    <MenuItem value="name-desc">{t('sortNameDesc')}</MenuItem>
                    {canSortByProducts && (
                        <MenuItem value="product-desc">{t('sortProductDesc')}</MenuItem>
                    )}
                </Select>
            </FormControl>
        </Stack>
    );
}

export function applyDestinationFilters<T extends DmcCardDto | DmcVisitorDto>(
    dmcs: ReadonlyArray<T>,
    filters: DestinationFiltersValue,
): T[] {
    const search = filters.search.trim().toLocaleLowerCase('fr');

    const filtered = dmcs.filter((dmc) => {
        if (search) {
            if (!dmc.name.toLocaleLowerCase('fr').includes(search)) return false;
        }
        if (filters.specialty && !dmc.specialties.some((s) => s.slug === filters.specialty)) {
            return false;
        }
        if (filters.market && !dmc.targetMarket.includes(filters.market)) {
            return false;
        }
        return true;
    });

    switch (filters.sort) {
        case 'name-asc':
            return [...filtered].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        case 'name-desc':
            return [...filtered].sort((a, b) => b.name.localeCompare(a.name, 'fr'));
        case 'product-desc':
            return [...filtered].sort((a, b) => {
                const aCount = 'productCount' in a ? a.productCount : 0;
                const bCount = 'productCount' in b ? b.productCount : 0;
                return bCount - aCount;
            });
        case 'relevance':
        default:
            return filtered;
    }
}
